import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  increment,
  Timestamp,
  runTransaction,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Post, PostLike, PostComment, CreatePostData } from '../types/social';
import { notifyPostLike, notifyPostComment } from './notificationsService';

// Posts
export const createPost = async (
  userId: string,
  userName: string,
  userFirstName: string | undefined,
  userLastName: string | undefined,
  userAvatar: string | undefined,
  postData: CreatePostData
): Promise<string> => {
  try {
    const postsRef = collection(db, 'posts');
    const docRef = await addDoc(postsRef, {
      userId,
      userName,
      userFirstName: userFirstName || '',
      userLastName: userLastName || '',
      userAvatar: userAvatar || '',
      content: postData.content,
      imageUrl: postData.imageUrl || '',
      eventId: postData.eventId || '',
      eventName: postData.eventName || '',
      recipeId: postData.recipeId || '',
      recipeName: postData.recipeName || '',
      type: postData.type,
      likesCount: 0,
      commentsCount: 0,
      favoritesCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Erro ao criar post:', error);
    throw error;
  }
};

export const getPosts = async (limitCount: number = 20): Promise<Post[]> => {
  try {
    const postsRef = collection(db, 'posts');
    const q = query(postsRef, orderBy('createdAt', 'desc'), limit(limitCount));
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
        updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
      } as Post;
    });
  } catch (error) {
    console.error('Erro ao buscar posts:', error);
    throw error;
  }
};

export const getUserPosts = async (userId: string): Promise<Post[]> => {
  try {
    const postsRef = collection(db, 'posts');
    const q = query(
      postsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
        updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
      } as Post;
    });
  } catch (error) {
    console.error('Erro ao buscar posts do usuário:', error);
    throw error;
  }
};

export const deletePost = async (postId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'posts', postId));
  } catch (error) {
    console.error('Erro ao deletar post:', error);
    throw error;
  }
};

export const getPostById = async (postId: string): Promise<Post | null> => {
  try {
    const postRef = doc(db, 'posts', postId);
    const postDoc = await getDoc(postRef);

    if (!postDoc.exists()) {
      return null;
    }

    const data = postDoc.data();
    return {
      id: postDoc.id,
      ...data,
      createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
      updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
    } as Post;
  } catch (error) {
    console.error('Erro ao buscar post por ID:', error);
    throw error;
  }
};

// Likes
export const likePost = async (
  postId: string,
  userId: string,
  userName: string,
  userAvatar?: string
): Promise<void> => {
  try {
    const likeDocRef = doc(db, 'posts', postId, 'likes', userId);
    const postRef = doc(db, 'posts', postId);

    const inserted = await runTransaction(db, async (tx) => {
      const likeSnap = await tx.get(likeDocRef);
      if (likeSnap.exists()) return false;
      tx.set(likeDocRef, {
        userId,
        userName,
        userAvatar: userAvatar || '',
        createdAt: serverTimestamp(),
      });
      tx.update(postRef, { likesCount: increment(1) });
      return true;
    });

    if (!inserted) return;

    const postDoc = await getDoc(postRef);
    if (postDoc.exists()) {
      const postData = postDoc.data();
      const postOwnerId = postData.userId;

      // Enviar notificação
      await notifyPostLike(
        userId,
        userName,
        userAvatar,
        postId,
        postOwnerId
      );
    }
  } catch (error) {
    console.error('Erro ao curtir post:', error);
    throw error;
  }
};

export const unlikePost = async (postId: string, userId: string): Promise<void> => {
  try {
    const likeDocRef = doc(db, 'posts', postId, 'likes', userId);
    const postRef = doc(db, 'posts', postId);

    await runTransaction(db, async (tx) => {
      const likeSnap = await tx.get(likeDocRef);
      if (!likeSnap.exists()) return;
      tx.delete(likeDocRef);
      tx.update(postRef, { likesCount: increment(-1) });
    });
  } catch (error) {
    console.error('Erro ao descurtir post:', error);
    throw error;
  }
};

export const getPostLikes = async (postId: string): Promise<PostLike[]> => {
  try {
    const likesRef = collection(db, 'posts', postId, 'likes');
    const q = query(likesRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        postId,
        ...data,
        createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
      } as PostLike;
    });
  } catch (error) {
    console.error('Erro ao buscar likes:', error);
    throw error;
  }
};

export const checkUserLikedPost = async (
  postId: string,
  userId: string
): Promise<boolean> => {
  try {
    const likesRef = collection(db, 'posts', postId, 'likes');
    const q = query(likesRef, where('userId', '==', userId), limit(1));
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    console.error('Erro ao verificar like:', error);
    return false;
  }
};

// Comentários
export const addComment = async (
  postId: string,
  userId: string,
  userName: string,
  userAvatar: string | undefined,
  content: string
): Promise<string> => {
  try {
    const commentsRef = collection(db, 'posts', postId, 'comments');
    const docRef = await addDoc(commentsRef, {
      userId,
      userName,
      userAvatar: userAvatar || '',
      content,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // Incrementar contador
    const postRef = doc(db, 'posts', postId);
    await updateDoc(postRef, {
      commentsCount: increment(1),
    });

    // Buscar dono do post para notificar
    const postDoc = await getDoc(postRef);
    if (postDoc.exists()) {
      const postData = postDoc.data();
      const postOwnerId = postData.userId;

      // Enviar notificação
      await notifyPostComment(
        userId,
        userName,
        userAvatar,
        content,
        postId,
        postOwnerId
      );
    }

    return docRef.id;
  } catch (error) {
    console.error('Erro ao adicionar comentário:', error);
    throw error;
  }
};

export const getPostComments = async (postId: string): Promise<PostComment[]> => {
  try {
    const commentsRef = collection(db, 'posts', postId, 'comments');
    const q = query(commentsRef, orderBy('createdAt', 'asc'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        postId,
        ...data,
        createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
        updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
      } as PostComment;
    });
  } catch (error) {
    console.error('Erro ao buscar comentários:', error);
    throw error;
  }
};

export const deleteComment = async (
  postId: string,
  commentId: string
): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'posts', postId, 'comments', commentId));

    // Decrementar contador
    const postRef = doc(db, 'posts', postId);
    await updateDoc(postRef, {
      commentsCount: increment(-1),
    });
  } catch (error) {
    console.error('Erro ao deletar comentário:', error);
    throw error;
  }
};

// Favorites
export const favoritePost = async (
  postId: string,
  userId: string,
  userName: string,
  userAvatar?: string
): Promise<void> => {
  try {
    const favoritesRef = collection(db, 'posts', postId, 'favorites');

    // Verificar se já favoritou
    const q = query(favoritesRef, where('userId', '==', userId), limit(1));
    const existingFavorite = await getDocs(q);
    if (!existingFavorite.empty) {
      return;
    }

    // Adicionar favorito
    await addDoc(favoritesRef, {
      userId,
      userName,
      userAvatar: userAvatar || '',
      createdAt: serverTimestamp(),
    });

    // Incrementar contador
    const postRef = doc(db, 'posts', postId);
    await updateDoc(postRef, {
      favoritesCount: increment(1),
    });
  } catch (error) {
    console.error('Erro ao favoritar post:', error);
    throw error;
  }
};

export const unfavoritePost = async (postId: string, userId: string): Promise<void> => {
  try {
    const favoritesRef = collection(db, 'posts', postId, 'favorites');
    const q = query(favoritesRef, where('userId', '==', userId));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return;
    }

    // Remover favorito
    await deleteDoc(snapshot.docs[0].ref);

    // Decrementar contador
    const postRef = doc(db, 'posts', postId);
    await updateDoc(postRef, {
      favoritesCount: increment(-1),
    });
  } catch (error) {
    console.error('Erro ao desfavoritar post:', error);
    throw error;
  }
};

export const checkUserFavoritedPost = async (
  postId: string,
  userId: string
): Promise<boolean> => {
  try {
    const favoritesRef = collection(db, 'posts', postId, 'favorites');
    const q = query(favoritesRef, where('userId', '==', userId), limit(1));
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    console.error('Erro ao verificar favorito:', error);
    return false;
  }
};

